import { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  RotateCcw,
  SlidersHorizontal,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Select } from "antd";
import { PtsProjectService } from "@/services/APIs";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import { Label } from "../../components/ui/label";
import {
  CHANGE_REQUEST_STATUS_OPTIONS,
  RAISED_TO_CLIENT_STATUS_OPTIONS,
} from "./utils";
import {
  FILTER_SELECT_CLASS,
  EMPTY_CHANGE_REQUEST_FILTERS,
  type ChangeRequestFilterValues,
} from "./ChangeRequestFilters.constants";

interface ChangeRequestFiltersProps {
  values: ChangeRequestFilterValues;
  onChange: (values: ChangeRequestFilterValues) => void;
  /** Hide status filter (e.g. Accountant entries already locked to Approved). */
  hideStatusFilter?: boolean;
  idPrefix?: string;
}

const ChangeRequestFilters = ({
  values,
  onChange,
  hideStatusFilter = false,
  idPrefix = "change-request",
}: ChangeRequestFiltersProps) => {
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const { projectId, status, raisedToClientStatus } = values;

  const { data: projectOptions = [], isLoading: projectLoading } = useQuery({
    queryKey: ["open-project-list"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
  });

  const projectSelectOptions = useMemo(
    () =>
      projectOptions.map(option => ({
        value: option.value,
        label: option.label,
      })),
    [projectOptions]
  );

  const statusOptions = useMemo(
    () =>
      CHANGE_REQUEST_STATUS_OPTIONS.map(value => ({
        value,
        label: value,
      })),
    []
  );

  const raisedOptions = useMemo(
    () =>
      RAISED_TO_CLIENT_STATUS_OPTIONS.map(value => ({
        value,
        label: value,
      })),
    []
  );

  const handleResetFilters = () => {
    onChange({ ...EMPTY_CHANGE_REQUEST_FILTERS });
  };

  const gridColsClass = hideStatusFilter ? "sm:grid-cols-2" : "sm:grid-cols-3";

  return (
    <Collapsible open={filtersExpanded} onOpenChange={setFiltersExpanded}>
      <Card className="border border-gray-200 bg-white shadow-sm">
        <CardContent className="px-4 py-2">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="hover:bg-muted/40 flex w-full items-center justify-between rounded-md text-left transition-colors"
              aria-expanded={filtersExpanded}
              aria-label="Toggle filters"
            >
              <div className="flex items-center gap-2 py-1">
                <SlidersHorizontal className="text-muted-foreground h-4 w-4" />
                <span className="text-sm font-semibold text-gray-800">
                  Filters
                </span>
              </div>
              {filtersExpanded ? (
                <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0" />
              ) : (
                <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
              )}
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent className="border-t border-gray-100 pb-2 pt-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
              <div
                className={`grid w-full grid-cols-1 gap-4 ${gridColsClass} lg:flex-1`}
              >
                <div className="flex w-full min-w-0 flex-col gap-1.5">
                  <Label
                    htmlFor={`${idPrefix}-project`}
                    className="text-xs font-medium text-gray-700"
                  >
                    Project
                  </Label>
                  <Select
                    id={`${idPrefix}-project`}
                    allowClear
                    placeholder={
                      projectLoading ? "Loading projects..." : "All projects"
                    }
                    value={projectId ?? undefined}
                    onChange={value =>
                      onChange({
                        ...values,
                        projectId: value || undefined,
                      })
                    }
                    className={FILTER_SELECT_CLASS}
                    style={{ width: "100%" }}
                    showSearch
                    loading={projectLoading}
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      String(option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={projectSelectOptions}
                    notFoundContent="No projects found"
                    listHeight={280}
                    popupMatchSelectWidth
                    getPopupContainer={trigger =>
                      trigger.parentElement ?? document.body
                    }
                  />
                </div>

                {!hideStatusFilter ? (
                  <div className="flex w-full min-w-0 flex-col gap-1.5">
                    <Label
                      htmlFor={`${idPrefix}-status`}
                      className="text-xs font-medium text-gray-700"
                    >
                      Status
                    </Label>
                    <Select
                      id={`${idPrefix}-status`}
                      allowClear
                      placeholder="All statuses"
                      value={status ?? undefined}
                      onChange={value =>
                        onChange({
                          ...values,
                          status: value || undefined,
                        })
                      }
                      className={FILTER_SELECT_CLASS}
                      style={{ width: "100%" }}
                      options={statusOptions}
                      listHeight={280}
                      popupMatchSelectWidth
                      getPopupContainer={trigger =>
                        trigger.parentElement ?? document.body
                      }
                    />
                  </div>
                ) : null}

                <div className="flex w-full min-w-0 flex-col gap-1.5">
                  <Label
                    htmlFor={`${idPrefix}-raised-status`}
                    className="text-xs font-medium text-gray-700"
                  >
                    Raised to client status
                  </Label>
                  <Select
                    id={`${idPrefix}-raised-status`}
                    allowClear
                    placeholder="All raised statuses"
                    value={raisedToClientStatus ?? undefined}
                    onChange={value =>
                      onChange({
                        ...values,
                        raisedToClientStatus: value || undefined,
                      })
                    }
                    className={FILTER_SELECT_CLASS}
                    style={{ width: "100%" }}
                    options={raisedOptions}
                    listHeight={280}
                    popupMatchSelectWidth
                    getPopupContainer={trigger =>
                      trigger.parentElement ?? document.body
                    }
                  />
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 w-full shrink-0 lg:w-[110px]"
                onClick={handleResetFilters}
              >
                <RotateCcw className="mr-1.5 h-4 w-4" />
                Reset
              </Button>
            </div>
          </CollapsibleContent>
        </CardContent>
      </Card>
    </Collapsible>
  );
};

export default ChangeRequestFilters;
